import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  InjectionToken,
  input,
  signal,
} from '@angular/core';
import {
  ChartData,
  ChartJsType,
  ChartOptions,
} from '../../models/chart.models';
import { ChartDataset } from 'chart.js';
import { htmlLegendPlugin } from '../../plugins/legend/legend.plugins';
import { ChartLegendComponent } from '../../plugins/legend/legend.component';
import { ChartBase } from '../base/base.chart';
import 'chartjs-adapter-luxon';
import { formatNumber } from '@angular/common';
import { TranslocoService } from '@jsverse/transloco';
import merge from 'lodash/merge';

export const CHANGE_VISIBILITY_TOKEN = new InjectionToken<(data: any) => void>(
  'changeVisibility',
);

@Component({
  selector: 'chart-line',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ChartLegendComponent, ChartBase],
  template: `
    <div class="flex flex-col gap-y-4 w-full">
      <chart-legend
        #legendRef
        [containerID]="'chart-legend'"
        [label]="legendLabel()"
        (changeVisibility)="onChangeVisibility($event)"
        (ready)="setLegend(legendRef)"
      />
      <chart-base
        [type]="type()"
        [data]="data()"
        [height]="height()"
        [options]="optionsComputed()"
        [plugins]="pluginsComputed()"
      />
    </div>
  `,
})
export class ChartLineComponent {
  private transloco = inject(TranslocoService);

  private changeVisibilityFn = inject(CHANGE_VISIBILITY_TOKEN, {
    optional: true,
  });

  onChangeVisibility(event: any) {
    this.changeVisibilityFn?.(event);
  }
  /**
   * Signal to store the HtmlLegendComponent instance.
   */
  legendComponent = signal<ChartLegendComponent | null>(null);

  /**
   * Signal to store the HtmlLegendComponent instance.
   */
  legendLabel = input<string | null>(null);

  /**
   * `labels`: Required input defining the data labels of the line chart.
   */
  labels = input<string[]>([]);

  /**
   * `datasets`: Required input defining the data values of the line chart.
   */
  datasets = input<ChartDataset<'line'>[]>([]);

  /**
   * `height`: Defines the height for the chart. Defaults to `280px`.
   */
  height = input<number>(420);

  /**
   * `units`: Defines the unit for the chart Y axis.
   */
  units = input<string>();

  /**
   * `numberPoints`: Defines the number of points to show on the chart.
   */
  numberPoints = input<number>(280);

  /**
   * `options`: Optional input to provide additional configuration options.
   */
  options = input<ChartOptions<'line'>>();

  /**
   * Computed `optionsComputed` property configuring responsiveness,
   * grid display, and scaling for the y-axis. Supports optional annotations.
   */
  optionsComputed = computed<ChartOptions<'line'>>(() => {
    const { min, max } = this.getMinMaxFromDatasets(this.datasets());
    const units = this.units();
    const lang = this.transloco.getActiveLang();
    const defaultOptions: ChartOptions<'line'> = {
      responsive: true,
      maintainAspectRatio: false,
      parsing: false,
      animation: false,
      spanGaps: true,
      // default element styles for line and points
      elements: {
        line: {
          borderWidth: 2,
        },
        point: {
          borderWidth: 2,
          radius: 2.5,
          hoverRadius: 5,
        },
      },
      plugins: {
        htmlLegend: {
          containerID: 'chart-legend',
          legendComponent: this.legendComponent(),
        },
        legend: {
          display: false,
        },
        decimation: {
          enabled: true,
          algorithm: 'lttb', // Or 'min-max'
          samples: this.numberPoints(), // Desired number of points
          threshold: 5,
        },
        zoom: {
          limits: {
            x: {
              min: min,
              max: max,
            },
          },
          pan: {
            enabled: true,
            mode: 'x', // Enable horizontal panning
          },
          zoom: {
            wheel: {
              enabled: true,
            },
            pinch: {
              enabled: true,
            },
            mode: 'x', // Enable horizontal zooming
          },
        },
      },
      scales: {
        x: {
          type: 'time',
          min: min,
          max: max,
          time: {
            displayFormats: {
              second: 'MMM d, yyyy HH:mm:ss',
              minute: 'MMM d, yyyy HH:mm',
              hour: 'MMM d, yyyy HH:mm',
              day: 'MMM d, yyyy',
              week: 'MMM d, yyyy',
              month: 'MMM yyyy',
              quarter: '[Q]Q yyyy',
              year: 'yyyy',
            },
            tooltipFormat: 'MMM d, HH:mm', // Tooltip format
          },
          grid: {
            display: false, // Removes x-axis gridlines.
          },
          ticks: {
            source: 'auto',
            // Disabled rotation for performance
            maxRotation: 45,
            autoSkip: true,
          },
        },
        y: {
          grid: {
            display: true, // Enables y-axis gridlines.
          },
          border: {
            dash: (context) =>
              context.index % 2 === 0 ? [Number.POSITIVE_INFINITY] : [3, 3],
          },
          ticks: {
            callback: function (value) {
              if (typeof value === 'number') {
                value = formatNumber(value, lang);
              }
              return units ? `${value} ${units}` : value;
            },
          },
        },
      },
    };

    return merge({}, defaultOptions, this.options() ?? {});
  });

  /**
   * Computed `data` property defining the structure of the bar chart.
   * Labels are empty since this is a generic bar chart.
   */
  data = computed<ChartData<'line'>>(() => ({
    labels: this.labels(),
    datasets: this.datasets(),
  }));

  /**
   * `type`: Specifies the chart type as `line`.
   */
  type = signal<ChartJsType>('line');

  /**
   * `pluginsComputed`: Dynamically resolves plugins with the updated HtmlLegendComponent reference.
   */
  pluginsComputed = computed(() => [htmlLegendPlugin]);

  /**
   * Set the LegendComponent reference to the signal.
   */
  setLegend(legendComponent: ChartLegendComponent) {
    this.legendComponent.set(legendComponent);
  }

  /**
   * Get the minimum and maximum values from a Chart.js dataset array.
   * @param datasets - Array of ChartDataset<'line'> objects.
   * @returns An object with min and max values for the dataset.
   */
  getMinMaxFromDatasets(datasets: ChartDataset<'line'>[]): {
    min: number;
    max: number;
  } {
    let minX = Number.POSITIVE_INFINITY;
    let maxX = Number.NEGATIVE_INFINITY;

    datasets.forEach((dataset) => {
      dataset.data.forEach((point) => {
        if (typeof point === 'object' && 'x' in point!) {
          const xValue =
            typeof point.x === 'number' ? point.x : new Date(point.x).getTime();
          minX = Math.min(minX, xValue);
          maxX = Math.max(maxX, xValue);
        }
      });
    });

    return { min: minX, max: maxX };
  }
}
